/**
 * Family Resources Calendar View Component
 *
 * Displays a daily timeline with columns for each family member.
 * Events appear in columns based on their family member assignments.
 * Uses @howljs/calendar-kit for the calendar rendering.
 *
 * Follows single responsibility principle: renders family-based resources calendar UI.
 */

import { Colors, ModernPalette } from '@/constants/theme';
import { TaskCountResolver } from '@/lib/utils/TaskCountResolver';
import { TaskIndicator } from '@/components/shared/TaskIndicator';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFamilyColumnPagination } from '@/hooks/use-family-column-pagination';
import { getLightBackground, getDarkBackground } from '@/lib/calendar/EventDisplayUtils';
import { ResourcesCalendarTheme } from '@/lib/calendar/ResourcesCalendarTheme';
import { LocalDateFormatter } from '@/lib/formatters/LocalDateFormatter';
import type { FamilyMemberResource, FamilyResourceCalendarEvent, AllDayEventsByFamilyMember } from '@/types/family-resource';
import type { CalendarKitHandle, DateOrDateTime, OnEventResponse, PackedEvent } from '@howljs/calendar-kit';
import {
  CalendarBody,
  CalendarContainer,
  CalendarHeader,
} from '@howljs/calendar-kit';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Image, Platform, RefreshControl, ScrollView, StyleSheet, Text, View } from 'react-native';
import { ResourcesCalendarHeader } from './ResourcesCalendarHeader';
import { FamilyAllDaySection } from './FamilyAllDaySection';
import { FamilyColumnHeader } from './FamilyColumnHeader';
import { HeaderShadowFade } from './HeaderShadowFade';
import { PaginationArrowWithPreview } from './PaginationArrowWithPreview';
import { OneTimeCallout } from '@/components/shared/OneTimeCallout';
import { useOneTimeCallout } from '@/hooks/use-one-time-callout';

type FamilyResourcesCalendarViewProps = {
  selectedDate: Date;
  resources: FamilyMemberResource[];
  timedEvents: FamilyResourceCalendarEvent[];
  allDayEventsByMember: AllDayEventsByFamilyMember;
  onDateChange: (date: Date) => void;
  onEventPress?: (eventId: string) => void;
  onCreateEvent?: (dateKey: string, time: string) => void;
  onAvatarPress?: (resource: FamilyMemberResource) => void;
  accentColor: string;
  onMenuPress: () => void;
  onTodayPress: () => void;
  onAddEventPress?: () => void;
  onHeaderTextPress?: () => void;
  showCalendarSelector: boolean;
  onRefresh?: () => Promise<void>;
  isRefreshing?: boolean;
};

export function FamilyResourcesCalendarView({
  selectedDate,
  resources,
  timedEvents,
  allDayEventsByMember,
  onDateChange,
  onEventPress,
  onCreateEvent,
  onAvatarPress,
  accentColor,
  onMenuPress,
  onTodayPress,
  onAddEventPress,
  showCalendarSelector,
  onHeaderTextPress,
  onRefresh,
  isRefreshing = false,
}: FamilyResourcesCalendarViewProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const insets = useSafeAreaInsets();
  const calendarRef = useRef<CalendarKitHandle>(null);
  const isSwipeDateChangeRef = useRef(false);
  const DEFAULT_SCROLL_HOUR = 6;
  const { isCalloutVisible, dismissCallout } = useOneTimeCallout('fimfam-ai:callout:day-nav');

  const theme = useMemo(() => {
    return ResourcesCalendarTheme.getTheme(colorScheme ?? 'light', accentColor);
  }, [colorScheme, accentColor]);

  // Use pagination hook for family member columns
  const {
    visibleResources,
    columnWidth,
    showPagination,
    goToNextPage,
    goToPrevPage,
    canGoNext,
    canGoPrev,
    nextPreviewResource,
    prevPreviewResource,
  } = useFamilyColumnPagination({
    resources,
    timeColumnWidth: theme.hourWidth,
  });

  const eventStyles = useMemo(() => {
    return ResourcesCalendarTheme.getEventStyles(colorScheme ?? 'light');
  }, [colorScheme]);

  // Memoize calendar theme to prevent recreation on every render
  const calendarTheme = useMemo(() => ({
    colors: {
      primary: theme.colors.primary,
      background: theme.colors.background,
      surface: theme.colors.surface,
      text: theme.colors.text,
      border: theme.colors.border,
      onBackground: theme.colors.text,
    },
    hourTextStyle: {
      fontFamily: getBodyFont(),
      fontSize: 11,
      fontWeight: '500' as const,
    },
    eventContainerStyle: {
      borderRadius: 0,
      overflow: 'hidden' as const,
    },
  }), [theme.colors]);

  const handleCalendarLoad = useCallback(() => {
    calendarRef.current?.goToHour(DEFAULT_SCROLL_HOUR, false);
  }, []);

  const handlePrevDay = useCallback(() => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    onDateChange(newDate);
  }, [selectedDate, onDateChange]);

  const handleNextDay = useCallback(() => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    onDateChange(newDate);
  }, [selectedDate, onDateChange]);

  const handleTodayPress = useCallback(() => {
    onTodayPress();
  }, [onTodayPress]);

  const handleHeaderTextPress = useCallback(() => {
    dismissCallout();
    onHeaderTextPress?.();
  }, [dismissCallout, onHeaderTextPress]);

  // Event press handler - extract original event ID
  const handleEventPress = useCallback(
    (event: OnEventResponse) => {
      if (onEventPress && event.id) {
        const eventWithOriginal = event as OnEventResponse & { originalEventId?: string };
        const originalId = eventWithOriginal.originalEventId || event.id;
        onEventPress(originalId);
      }
    },
    [onEventPress]
  );

  const handleCalendarDateChange = useCallback(
    (date: string) => {
      const newDate = new Date(date);
      const currentLocalDate = LocalDateFormatter.toDateKey(selectedDate);
      const newLocalDate = LocalDateFormatter.toDateKey(newDate);

      if (currentLocalDate !== newLocalDate) {
        isSwipeDateChangeRef.current = true;
        onDateChange(newDate);
      }
    },
    [onDateChange, selectedDate]
  );

  // Double-tap detection for background press to create event
  const lastBackgroundTapRef = useRef<number>(0);
  const DOUBLE_TAP_THRESHOLD = 300;

  const handlePressBackground = useCallback((props: DateOrDateTime) => {
    const now = Date.now();
    if (now - lastBackgroundTapRef.current < DOUBLE_TAP_THRESHOLD) {
      lastBackgroundTapRef.current = 0;
      if ('dateTime' in props && props.dateTime && onCreateEvent) {
        const parsed = new Date(props.dateTime);
        const roundedMinutes = Math.floor(parsed.getMinutes() / 30) * 30;
        const dateKey = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
        const time = `${String(parsed.getHours()).padStart(2, '0')}:${String(roundedMinutes).padStart(2, '0')}`;
        onCreateEvent(dateKey, time);
      }
    } else {
      lastBackgroundTapRef.current = now;
    }
  }, [onCreateEvent]);

  const calendarKey = 'family-calendar-container';

  // Navigate calendar when selectedDate changes
  // For swipe: snap without animation (already navigated visually)
  // For external changes (Today/arrows): animate to date
  useEffect(() => {
    if (calendarRef.current) {
      const dateKey = LocalDateFormatter.toDateKey(selectedDate);
      const shouldAnimate = !isSwipeDateChangeRef.current;
      isSwipeDateChangeRef.current = false;

      calendarRef.current.goToDate({
        date: dateKey,
        animatedDate: shouldAnimate,
        hourScroll: false,
      });
    }
  }, [selectedDate]);

  

  // Filter events to only show those for visible resources
  const visibleResourceIds = useMemo(() => {
    return new Set(visibleResources.map(r => r.id));
  }, [visibleResources]);

  const filteredTimedEvents = useMemo(() => {
    return timedEvents.filter(event => visibleResourceIds.has(event.resourceId));
  }, [timedEvents, visibleResourceIds]);

  // Transform events for the calendar library
  const isLight = (colorScheme ?? 'light') === 'light';
  const transformedEvents = useMemo(() => {
    return filteredTimedEvents.map(event => {
      const originalColor = event.color || ModernPalette.eventBlue;
      return {
        ...event,
        id: event.id,
        color: isLight ? getLightBackground(originalColor) : getDarkBackground(originalColor),
        _accentColor: originalColor,
      };
    });
  }, [filteredTimedEvents, isLight]);

  // Map visible resources to library format
  const libraryResources = useMemo(() => {
    return visibleResources.map(r => ({
      id: r.id,
      title: r.name,
      color: r.color,
    }));
  }, [visibleResources]);

  // Calculate calendar container width
  const calendarWidth = useMemo(() => {
    return columnWidth * visibleResources.length + theme.hourWidth;
  }, [columnWidth, visibleResources.length, theme.hourWidth]);

  // Memoize static text styles for renderEvent
  const eventTitleStyle = useMemo(
    () => [eventStyles.title],
    [eventStyles.title]
  );
  const eventLocationStyle = useMemo(
    () => [eventStyles.location],
    [eventStyles.location]
  );

  const renderEvent = useCallback(
    (event: PackedEvent & { _accentColor?: string }) => {
      const eventAccentColor = event._accentColor || ModernPalette.eventBlue;

      const taskCount = TaskCountResolver.fromCounts(
        (event as PackedEvent & { openTaskCount?: number }).openTaskCount,
        (event as PackedEvent & { totalTaskCount?: number }).totalTaskCount,
      );

      return (
        <View
          style={[
            eventContainerBaseStyle,
            eventStyles.container,
            { borderColor: eventAccentColor },
          ]}
        >
          {taskCount && (
            <View style={eventTaskIndicatorStyle}>
              <TaskIndicator taskCount={taskCount} size="small" />
            </View>
          )}
          <Text style={eventTitleStyle} numberOfLines={2}>
            {event.title}
          </Text>
          {event.location && (
            <Text style={eventLocationStyle} numberOfLines={1}>
              {event.location}
            </Text>
          )}
        </View>
      );
    },
    [eventStyles.container, eventTitleStyle, eventLocationStyle]
  );

  // Empty state - no family members
  if (resources.length === 0) {
    return (
      <View style={styles.container}>
        <ResourcesCalendarHeader
          selectedDate={selectedDate}
          onPrevDay={handlePrevDay}
          onNextDay={handleNextDay}
          onTodayPress={handleTodayPress}
          onMenuPress={onMenuPress}
          onAddEventPress={onAddEventPress}
          onHeaderTextPress={onHeaderTextPress}
          accentColor={accentColor}
          showCalendarSelector={showCalendarSelector}
        />
        <View style={styles.emptyContainer}>
          <Image
            source={require('@/assets/images/fim-sleep-transparent.png')}
            style={styles.emptyImage}
            resizeMode="contain"
          />
          <Text style={styles.emptyText}>No family members</Text>
          <Text style={styles.emptySubtext}>
            Add family members from the Family tab
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Extended rounded header: wraps date nav + avatars + all-day row,
          with the rounded bottom + shadow sitting just above the timeline.
          Two-layer pattern: outer carries the shadow + rounded shape, inner
          clips child borders (e.g. all-day section's 1px bottom border) so
          they don't bleed across the rounded corners. */}
      <View
        style={[
          styles.extendedHeader,
          { backgroundColor: palette.cardBackground },
        ]}
      >
       <View
         style={[
           styles.extendedHeaderClip,
           { backgroundColor: palette.cardBackground, paddingTop: insets.top },
         ]}
       >
        <ResourcesCalendarHeader
          selectedDate={selectedDate}
          onPrevDay={handlePrevDay}
          onNextDay={handleNextDay}
          onTodayPress={handleTodayPress}
          onMenuPress={onMenuPress}
          onAddEventPress={onAddEventPress}
          onHeaderTextPress={onHeaderTextPress ? handleHeaderTextPress : undefined}
          accentColor={accentColor}
          showCalendarSelector={showCalendarSelector}
          bareContainer
        />

        {/* Column headers with inline pagination arrows */}
        <View style={[styles.columnHeadersRow, { backgroundColor: palette.cardBackground }]}>
          {/* Left arrow in time column space */}
          <View style={[styles.arrowContainer, { width: theme.hourWidth }]}>
            {showPagination && (
              <PaginationArrowWithPreview
                direction="left"
                onPress={goToPrevPage}
                disabled={!canGoPrev}
                previewMember={prevPreviewResource}
              />
            )}
          </View>

          {/* Family member column headers */}
          <FamilyColumnHeader
            visibleResources={visibleResources}
            columnWidth={columnWidth}
            textColor={theme.colors.text}
            onAvatarPress={onAvatarPress}
          />

          {/* Right arrow after columns */}
          {showPagination && (
            <View style={styles.arrowContainer}>
              <PaginationArrowWithPreview
                direction="right"
                onPress={goToNextPage}
                disabled={!canGoNext}
                previewMember={nextPreviewResource}
              />
            </View>
          )}
        </View>

        {/* All-day events section */}
        <View style={{ backgroundColor: palette.cardBackground }}>
          <FamilyAllDaySection
            visibleResources={visibleResources}
            allDayEventsByMember={allDayEventsByMember}
            onEventPress={onEventPress}
            hourWidth={theme.hourWidth}
            columnWidth={columnWidth}
            borderColor={theme.colors.border}
          />
        </View>
       </View>
      </View>

      <View style={styles.calloutAnchor} pointerEvents="box-none">
        <OneTimeCallout
          visible={isCalloutVisible}
          text="Tap the date above to go to week view."
          onDismiss={dismissCallout}
          pointer="up"
        />
      </View>

      {/* Relative wrapper so the HeaderShadowFade overlay can sit over the
          top of the timeline; content scrolling up fades into the page bg. */}
      <View style={styles.scrollFadeWrapper}>
       <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          onRefresh ? (
            <RefreshControl
              refreshing={isRefreshing}
              onRefresh={onRefresh}
              tintColor={accentColor}
              colors={[accentColor]}
            />
          ) : undefined
        }
        scrollEnabled={false}
        showsVerticalScrollIndicator={false}
      >
        {/* Main calendar grid - swipe changes date */}
        <View style={{ flex: 1, marginTop: -61, zIndex: 1 }}>
          <CalendarContainer
            ref={calendarRef}
            key={`${calendarKey}-${colorScheme}`}
            numberOfDays={1}
            calendarWidth={calendarWidth}
            initialDate={LocalDateFormatter.toDateKey(selectedDate)}
            resources={libraryResources}
            events={transformedEvents}
            onPressEvent={handleEventPress}
            onPressBackground={handlePressBackground}
            onDateChanged={handleCalendarDateChange}
            onLoad={handleCalendarLoad}
            {...({
              hourHeight: theme.hourHeight,
              hourWidth: theme.hourWidth,
              startHour: theme.startHour,
              endHour: theme.endHour,
            } as Record<string, unknown>)}
            scrollToNow={false}
            scrollByDay={true}
            allowHorizontalSwipe={true}
            spaceFromBottom={100}
            theme={calendarTheme}
          >
            <CalendarHeader renderHeaderItem={() => <View style={{ height: 0 }} />} />
            <CalendarBody renderEvent={renderEvent} />
          </CalendarContainer>
        </View>
       </ScrollView>
       <HeaderShadowFade />
      </View>
    </View>
  );
}

// Static style objects to avoid recreation on every render
const eventContainerBaseStyle = { flex: 1, margin: -1 } as const;
const eventTaskIndicatorStyle = { position: 'absolute' as const, top: 3, right: 3, zIndex: 1 } as const;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  extendedHeader: {
    zIndex: 5,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.18,
        shadowRadius: 8,
      },
      android: {
        elevation: 6,
      },
      default: {},
    }),
  },
  extendedHeaderClip: {
    overflow: 'hidden',
  },
  calloutAnchor: {
    position: 'relative',
    zIndex: 101,
  },
  scrollFadeWrapper: {
    flex: 1,
    position: 'relative',
  },
  scrollContainer: {
    flex: 1,
  },
  scrollContent: {
    flex: 1,
  },
  columnHeadersRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  arrowContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    gap: 12,
  },
  emptyImage: {
    width: 100,
    height: 100,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    fontWeight: '600',
    color: ModernPalette.textPrimary,
  },
  emptySubtext: {
    fontSize: 12,
    color: ModernPalette.textSecondary,
    textAlign: 'center',
  },
});
